import { z } from 'zod';
import { ENERGY_CERTIFICATE_TYPES, ENERGY_CLASSES, EXTERNAL_RESERVATION_STATUSES, MEDIA_ITEM_KINDS, TENANCY_STATUSES, UNIT_AVAILABILITIES, UNIT_COST_ITEM_KINDS, UNIT_COST_ITEM_TAX_TREATMENTS, UNIT_EXTERNAL_BLOCKS, UNIT_LINK_KINDS, UNIT_SUBTYPES, UNIT_USAGES, amenitiesSchema, } from '../property-data.js';
/**
 * API record additions of the property/unit data model extension (migration 0029, docs/dev/api-properties.md
 * „Data model extension"). Returned by `GET /properties/:id`, `GET /properties/:id/units`, `GET /units/:id`.
 */
/** Binding of a property/unit to an external system; null when the record was not imported. */
export const externalSourceSchema = z
    .object({
    source: z.literal('teamproq'),
    connectionId: z.string(),
    connectionName: z.string(),
    mode: z.enum(['import_only', 'bidirectional']),
    lastSyncedAt: z.string().nullable(),
    /** Gone = no longer delivered by the external system (`external_ref.gone_at`). */
    goneAt: z.string().nullable(),
    /** API field names that the external system owns (locked in the UI while `mode = import_only`). */
    managedFields: z.array(z.string()),
})
    .nullable();
export const unitAvailabilitySchema = z.enum(UNIT_AVAILABILITIES);
export const mediaItemRecordSchema = z.object({
    id: z.string(),
    fileId: z.string(),
    kind: z.enum(MEDIA_ITEM_KINDS),
    kindLabel: z.string(),
    position: z.number().int(),
    isCover: z.boolean(),
    caption: z.string().nullable(),
    /** `/api/files/<fileId>` — the original. */
    url: z.string(),
    /** Downscaled WebP variants (`?variant=thumb|medium|large`, 400/1200/2400 px long edge); the original until rendered. */
    thumbUrl: z.string(),
    mediumUrl: z.string(),
    largeUrl: z.string(),
    /** Pixel size of the original (EXIF orientation applied); null until the variants are rendered. */
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    name: z.string(),
    mime: z.string(),
    size: z.number().int(),
    /** true when the item came from an external system (cannot be deleted while `import_only`). */
    imported: z.boolean(),
});
export const unitCostItemRecordSchema = z.object({
    id: z.string(),
    kind: z.enum(UNIT_COST_ITEM_KINDS),
    kindLabel: z.string(),
    label: z.string(),
    amountCents: z.number().int().nullable(),
    percent: z.number().nullable(),
    taxTreatment: z.enum(UNIT_COST_ITEM_TAX_TREATMENTS),
    taxTreatmentLabel: z.string(),
    includingVat: z.boolean(),
    risePct: z.number().nullable(),
    riseIntervalMonths: z.number().int().nullable(),
    riseDelayMonths: z.number().int().nullable(),
    position: z.number().int(),
    imported: z.boolean(),
});
export const unitLinkRecordSchema = z.object({
    kind: z.enum(UNIT_LINK_KINDS),
    relatedUnitId: z.string(),
    number: z.string(),
    type: z.string(),
    typeLabel: z.string(),
    availability: unitAvailabilitySchema,
    availabilityLabel: z.string(),
    imported: z.boolean(),
});
export const externalReservationRecordSchema = z.object({
    id: z.string(),
    source: z.literal('teamproq'),
    status: z.enum(EXTERNAL_RESERVATION_STATUSES),
    statusLabel: z.string(),
    beginOn: z.string().nullable(),
    endOn: z.string().nullable(),
    position: z.number().int().nullable(),
    broker: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string(),
});
export const tenancyRecordSchema = z.object({
    id: z.string(),
    status: z.enum(TENANCY_STATUSES),
    statusLabel: z.string(),
    tenantName: z.string().nullable(),
    tenantEmail: z.string().nullable(),
    tenantPhone: z.string().nullable(),
    tenantContactId: z.string().nullable(),
    beginOn: z.string().nullable(),
    endOn: z.string().nullable(),
    rentColdCents: z.number().int().nullable(),
    serviceChargeCents: z.number().int().nullable(),
    heatingCostCents: z.number().int().nullable(),
    depositCents: z.number().int().nullable(),
    notes: z.string().nullable(),
    imported: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Raw values of the new unit columns (part of `UnitDetail.fields`, editable through `PATCH /units/:id`). */
export const unitDataFieldsSchema = z.object({
    subtype: z.enum(UNIT_SUBTYPES).nullable(),
    usage: z.enum(UNIT_USAGES).nullable(),
    street: z.string().nullable(),
    houseNumber: z.string().nullable(),
    floorPosition: z.string().nullable(),
    bathrooms: z.number().int().nullable(),
    landAreaSqm: z.number().nullable(),
    salesReleased: z.boolean(),
    externalBlock: z.enum(UNIT_EXTERNAL_BLOCKS).nullable(),
    priceLandCents: z.number().int().nullable(),
    priceExistingBuildingCents: z.number().int().nullable(),
    priceNewConstructionCents: z.number().int().nullable(),
    priceFurnitureCents: z.number().int().nullable(),
    priceOutdoorFacilitiesCents: z.number().int().nullable(),
    amenities: amenitiesSchema,
    energyCertificateType: z.enum(ENERGY_CERTIFICATE_TYPES).nullable(),
    energyValueKwhSqm: z.number().nullable(),
    energyClass: z.enum(ENERGY_CLASSES).nullable(),
    energyCarriers: z.array(z.string()),
    heatingSystems: z.array(z.string()),
    kfwStandard: z.string().nullable(),
});
/** Raw values of the new property columns (part of `PropertyRecord.fields`). */
export const propertyDataFieldsSchema = z.object({
    coOwnershipSharesTotal: z.number().nullable(),
    yearRenovated: z.number().int().nullable(),
    locationDescription: z.string().nullable(),
    surroundingsDescription: z.string().nullable(),
    objectDescription: z.string().nullable(),
    specialFeatures: z.string().nullable(),
    additionalInformation: z.string().nullable(),
});
//# sourceMappingURL=property-data.js.map